import React, { useState } from 'react';
import { Button, Modal, Tooltip, message } from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import BrandAnalyticsChart from '../BrandAnalytics/BrandAnalyticsChart';
import { ICustomBrandChart, IBrandAnalyticsMonthlyData, IBrandAnalyticsMetricOption } from '../../services/nestjs-base-api';
import { useUpdateCustomBrandChartMutation } from '../../services/nestjs-base-api';
import { useAuth } from '../../hooks/useAuth';

const { confirm } = Modal;

interface CustomChartCardProps {
    chart: ICustomBrandChart;
    data: IBrandAnalyticsMonthlyData[];
    availableMetrics: IBrandAnalyticsMetricOption[];
    brandName: string;
    brandHash: string;
    isFirst: boolean;
    isLast: boolean;
    onEdit: (chart: ICustomBrandChart) => void;
    onDelete: (chartId: number) => void;
    onMoveUp: (chartId: number) => void;
    onMoveDown: (chartId: number) => void;
    // Subscription tier restrictions
    isElementLocked?: (elementName: string) => boolean;
    hasUnrestrictedAccess?: boolean;
    // Low response count warnings
    minResponseThreshold?: number;
    lowConfidenceThreshold?: number;
}

const CustomChartCard: React.FC<CustomChartCardProps> = ({
    chart,
    data,
    availableMetrics: topLevelAvailableMetrics,
    brandName,
    brandHash,
    isFirst,
    isLast,
    onEdit,
    onDelete,
    onMoveUp,
    onMoveDown,
    isElementLocked,
    hasUnrestrictedAccess = true,
    minResponseThreshold,
    lowConfidenceThreshold,
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const [updateChart] = useUpdateCustomBrandChartMutation();

    // Local state for metric selection and drill-down
    const [selectedMetrics, setSelectedMetrics] = useState<string[]>(chart.selectedMetrics || []);
    const [drillDownLevel, setDrillDownLevel] = useState<'latent' | 'measuredElement'>('latent');
    const [parentMetric, setParentMetric] = useState<string | null>(null);
    const [previousSelectedMetrics, setPreviousSelectedMetrics] = useState<string[]>([]);
    const [availableMetrics, setAvailableMetrics] = useState<IBrandAnalyticsMetricOption[]>(topLevelAvailableMetrics);

    // Update available metrics when parent updates them (only when not in drill-down)
    React.useEffect(() => {
        if (!parentMetric) {
            setAvailableMetrics(topLevelAvailableMetrics);
        }
    }, [topLevelAvailableMetrics, parentMetric]);

    // Auto-save metrics when they change
    const handleMetricsChange = async (newMetrics: string[]) => {
        if (!user?.jwtToken) return;

        setSelectedMetrics(newMetrics);

        try {
            await updateChart({
                jwtToken: user.jwtToken,
                id: chart.id,
                brandHash,
                data: {
                    chartName: chart.chartName,
                    selectedMetrics: newMetrics,
                },
            }).unwrap();
        } catch (error) {
            console.error('Failed to auto-save chart metrics:', error);
            // Silently fail - we don't want to interrupt the user's workflow
        }
    };

    const handleDelete = () => {
        confirm({
            title: intl.formatMessage({ id: 'customCharts.delete.confirm.title' }),
            icon: <ExclamationCircleOutlined />,
            content: intl.formatMessage(
                { id: 'customCharts.delete.confirm.message' },
                { name: chart.chartName }
            ),
            okText: intl.formatMessage({ id: 'customCharts.delete.confirm.ok' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'common.cancelButton' }),
            onOk() {
                onDelete(chart.id);
            },
        });
    };

    const handleDrillDown = (
        metric: string,
        constituents: string[],
        currentSelectedMetrics: string[]
    ) => {
        // Filter out locked constituent elements for restricted users
        const unlockedConstituents = isElementLocked
            ? constituents.filter(element => !isElementLocked(element))
            : constituents;

        // If all constituents are locked, show a message instead of drilling down
        if (unlockedConstituents.length === 0 && constituents.length > 0) {
            message.info(intl.formatMessage({ id: 'subscription.upgrade.allConstituentsRequireUpgrade' }));
            return;
        }

        setPreviousSelectedMetrics(currentSelectedMetrics);
        setParentMetric(metric);
        setDrillDownLevel('measuredElement');

        // Create drill-down metrics for the constituents (input elements) - only unlocked ones
        const drillDownMetrics: IBrandAnalyticsMetricOption[] = unlockedConstituents.map(inputElement => ({
            key: inputElement,
            label: inputElement,
            type: 'latentElement', // Use existing type for input elements
            drillDownAvailable: false // Input elements are the deepest level we show
        }));

        // Show info if some constituents were filtered out
        const lockedCount = constituents.length - unlockedConstituents.length;
        if (lockedCount > 0) {
            message.info(intl.formatMessage({ id: 'subscription.upgrade.elementsHidden' }, { count: lockedCount }));
        }

        setAvailableMetrics(drillDownMetrics);
    };

    const handleBackClick = () => {
        setParentMetric(null);
        setDrillDownLevel('latent');
        setPreviousSelectedMetrics([]);

        // Restore top-level metrics
        setAvailableMetrics(topLevelAvailableMetrics);
    };

    const renderHeaderActions = () => (
        <>
            <Tooltip title={intl.formatMessage({ id: 'customCharts.card.edit' })}>
                <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => onEdit(chart)}
                />
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: 'customCharts.card.delete' })}>
                <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleDelete}
                />
            </Tooltip>
            {!isFirst && (
                <Tooltip title={intl.formatMessage({ id: 'customCharts.card.moveUp' })}>
                    <Button
                        type="text"
                        icon={<ArrowUpOutlined />}
                        onClick={() => onMoveUp(chart.id)}
                    />
                </Tooltip>
            )}
            {!isLast && (
                <Tooltip title={intl.formatMessage({ id: 'customCharts.card.moveDown' })}>
                    <Button
                        type="text"
                        icon={<ArrowDownOutlined />}
                        onClick={() => onMoveDown(chart.id)}
                    />
                </Tooltip>
            )}
        </>
    );

    return (
        <div style={{ marginBottom: 48 }}>
            <BrandAnalyticsChart
                data={data}
                availableMetrics={availableMetrics}
                brandName={brandName}
                brandHash={brandHash}
                drillDownLevel={drillDownLevel}
                parentMetric={parentMetric}
                onDrillDown={handleDrillDown}
                onBackClick={handleBackClick}
                previousSelectedMetrics={previousSelectedMetrics}
                // Only use controlled mode when NOT in drill-down
                // During drill-down, let the chart manage its own selection (uncontrolled mode)
                initialSelectedMetrics={parentMetric ? undefined : selectedMetrics}
                onMetricsChange={parentMetric ? undefined : handleMetricsChange}
                customTitle={chart.chartName}
                headerActions={renderHeaderActions()}
                isElementLocked={isElementLocked}
                hasUnrestrictedAccess={hasUnrestrictedAccess}
                minResponseThreshold={minResponseThreshold}
                lowConfidenceThreshold={lowConfidenceThreshold}
            />
        </div>
    );
};

export default CustomChartCard;
